import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CalendarClock, CheckCircle2, MapPin, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { v: "day", l: "Day" },
  { v: "week", l: "Week" },
  { v: "month", l: "Month" },
  { v: "year", l: "Year" },
];

function formatStart(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * Admin-only meetings report. Shows a Day/Week/Month/Year selector
 * and splits the result into Scheduled vs Completed lists with hydrated
 * client + owner names.
 */
export default function MeetingsReport() {
  const [period, setPeriod] = useState("month");
  const [tab, setTab] = useState("scheduled");
  // Sub-type filter: null = all, "demo", "meeting", "site_visit". When
  // "demo" is selected we still hit the same `/admin/reports/meetings`
  // endpoint with a query param so the UI remains a single component.
  const [typeFilter, setTypeFilter] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async (p, typeF) => {
    setLoading(true);
    try {
      const params = { period: p };
      if (typeF) params.meeting_type = typeF;
      const { data } = await api.get("/admin/reports/meetings", { params });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(period, typeFilter); }, [period, typeFilter]);

  const markComplete = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/meetings/${id}/complete`);
      toast.success("Meeting marked complete");
      load(period, typeFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark complete");
    } finally { setBusyId(null); }
  };
  const reopen = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/meetings/${id}/reopen`);
      toast.success("Reopened — back to scheduled");
      load(period, typeFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen");
    } finally { setBusyId(null); }
  };

  const list = (data?.[tab] || []);

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mb-6" data-testid="meetings-report">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <CalendarClock className="h-3 w-3" /> Scheduled meetings
            </p>
            <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mt-0.5">Meetings tracker</h3>
            <p className="text-xs text-muted-foreground mt-1">Across the whole team. Pick a window — see what's booked vs what actually happened.</p>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="rounded-2xl bg-muted/50 p-0.5 h-9">
              {PERIODS.map((p) => (
                <TabsTrigger
                  key={p.v}
                  value={p.v}
                  data-testid={`meetings-period-${p.v}`}
                  className="rounded-xl px-3 text-xs font-semibold data-[state=active]:bg-background data-[state=active]:shadow"
                >
                  {p.l}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Type filter — let admin slice into demos / meetings / site visits */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="meeting-type-filter">
          {[
            { k: null, label: "All", testid: "all" },
            { k: "demo", label: "Demos", testid: "demo" },
            { k: "meeting", label: "Meetings", testid: "meeting" },
            { k: "site_visit", label: "Site visits", testid: "site_visit" },
          ].map((f) => (
            <button
              key={f.testid}
              type="button"
              onClick={() => setTypeFilter(f.k)}
              className={`px-3 h-8 rounded-full text-xs font-semibold tap transition ${typeFilter === f.k ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground"}`}
              data-testid={`meeting-type-filter-${f.testid}`}
            >{f.label}</button>
          ))}
        </div>

        {/* By-type breakdown card — surfaces demo activity at-a-glance */}
        {data?.by_type && (
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="by-type-breakdown">
            {[
              { k: "demo", label: "Demos", color: "text-primary-700", bg: "bg-primary-50/60 border-primary-200/60" },
              { k: "meeting", label: "Meetings", color: "text-stone-700", bg: "bg-stone-100 border-stone-200" },
              { k: "site_visit", label: "Site visits", color: "text-amber-800", bg: "bg-amber-50/60 border-amber-200/60" },
            ].map((row) => {
              const sched = data.by_type.scheduled?.[row.k] || 0;
              const done = data.by_type.completed?.[row.k] || 0;
              const total = sched + done;
              return (
                <div key={row.k} className={`p-3 rounded-2xl border ${row.bg}`} data-testid={`by-type-${row.k}`}>
                  <p className={`text-[10px] uppercase tracking-[0.18em] font-bold ${row.color}`}>{row.label}</p>
                  <p className="font-display text-2xl font-black mt-1">{total}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{sched} scheduled · {done} completed</p>
                </div>
              );
            })}
          </div>
        )}
        {/* Status tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setTab("scheduled")}
            className={`p-3 rounded-2xl border text-left tap transition ${tab === "scheduled" ? "border-primary bg-primary-50" : "border-border/60 hover:bg-muted/40"}`}
            data-testid="meetings-tab-scheduled"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Scheduled</p>
            <p className="font-display text-2xl font-black mt-1">{data?.scheduled_count ?? 0}</p>
          </button>
          <button
            type="button"
            onClick={() => setTab("completed")}
            className={`p-3 rounded-2xl border text-left tap transition ${tab === "completed" ? "border-primary bg-primary-50" : "border-border/60 hover:bg-muted/40"}`}
            data-testid="meetings-tab-completed"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Completed</p>
            <p className="font-display text-2xl font-black text-emerald-600 mt-1">{data?.completed_count ?? 0}</p>
          </button>
          {(data?.cancelled_count || 0) > 0 && (
            <button
              type="button"
              onClick={() => setTab("cancelled")}
              className={`p-3 rounded-2xl border text-left tap transition ${tab === "cancelled" ? "border-primary bg-primary-50" : "border-border/60 hover:bg-muted/40"}`}
              data-testid="meetings-tab-cancelled"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Cancelled</p>
              <p className="font-display text-2xl font-black text-red-600 mt-1">{data.cancelled_count}</p>
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 rounded-2xl" />
            <Skeleton className="h-12 rounded-2xl" />
            <Skeleton className="h-12 rounded-2xl" />
          </div>
        ) : list.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid={`meetings-empty-${tab}`}>
            No {tab} meetings in the selected {period}.
          </div>
        ) : (
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid={`meetings-list-${tab}`}>
            {list.map((m) => (
              <div key={m.id} className="p-3 rounded-2xl border border-border/60 hover:bg-muted/30 transition" data-testid={`meeting-row-${m.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm truncate">{m.title}</p>
                      {tab === "completed" && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-bold">
                          <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Completed
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {m.client_name && <span>{m.client_name}</span>}
                      {m.client_name && (m.location || m.owner_name) && <span> · </span>}
                      {m.location && <span className="inline-flex items-center"><MapPin className="h-3 w-3 mr-0.5" />{m.location}</span>}
                      {m.location && m.owner_name && <span> · </span>}
                      {m.owner_name && <span className="inline-flex items-center"><UserIcon className="h-3 w-3 mr-0.5" />{m.owner_name}</span>}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold text-foreground">{formatStart(m.start_at)}</p>
                    {tab === "completed" && m.completed_at && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Closed {formatStart(m.completed_at)}</p>
                    )}
                    <div className="mt-1.5">
                      {tab === "scheduled" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markComplete(m.id)}
                          disabled={busyId === m.id}
                          className="rounded-xl h-7 text-[10px] tap text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          data-testid={`meeting-complete-${m.id}`}
                        >
                          {busyId === m.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                          Mark complete
                        </Button>
                      ) : tab === "completed" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => reopen(m.id)}
                          disabled={busyId === m.id}
                          className="rounded-xl h-7 text-[10px] tap text-muted-foreground hover:text-foreground"
                          data-testid={`meeting-reopen-${m.id}`}
                        >
                          Reopen
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
